import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, CheckCircle2, XCircle, Loader2, Mail, RefreshCw } from "lucide-react";
import ParticleBackground from "@/components/ParticleBackground";
const logo = "/assets/Touchworks-b.svg";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // States
  const [step, setStep] = useState("verifying"); // verifying | set-password | success | error | resend
  const [error, setError] = useState("");
  const [errorCode, setErrorCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [resendEmail, setResendEmail] = useState("");
  const [resendMessage, setResendMessage] = useState("");
  const [isResending, setIsResending] = useState(false);

  // Password form
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password validation
  const passwordChecks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    number: /[0-9]/.test(password),
    match: password === confirmPassword && password.length > 0,
  };

  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  // Helper: decode JWT payload without verification (frontend-only, backend validates on set-password)
  function decodeJwtPayload(jwt) {
    try {
      const parts = jwt.split(".");
      if (parts.length !== 3) return null;
      const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
      return payload;
    } catch {
      return null;
    }
  }

  // Verify email on mount
  useEffect(() => {
    if (!token) {
      setStep("error");
      setError("Token de verificação não encontrado. Verifique o link no seu email.");
      return;
    }

    // Check if token is a JWT set-password token (from resend-verification flow)
    const jwtPayload = decodeJwtPayload(token);
    if (jwtPayload && jwtPayload.purpose === "set-password") {
      // Skip email verification, go directly to set-password
      setUserData({
        setPasswordToken: token,
        email: jwtPayload.email,
        userId: jwtPayload.userId,
      });
      setStep("set-password");
      return;
    }

    verifyEmail();
  }, [token]);

  async function verifyEmail() {
    try {
      setStep("verifying");
      const response = await fetch("/api/auth/verify-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        setStep("error");
        setErrorCode(data.code || "");
        if (data.code === "TOKEN_EXPIRED") {
          setError("O link de verificação expirou. Solicite um novo abaixo.");
        } else if (data.code === "INVALID_TOKEN") {
          setError("Link inválido ou já utilizado. Se você já verificou sua conta, faça login.");
        } else {
          setError(data.error || "Erro ao verificar email.");
        }
        return;
      }

      setUserData(data.data);
      setStep("set-password");
    } catch (err) {
      console.error("Verification error:", err);
      setStep("error");
      setError("Erro de conexão. Tente novamente mais tarde.");
    }
  }

  async function handleResendVerification(e) {
    e.preventDefault();
    if (!resendEmail || isResending) return;

    setIsResending(true);
    setResendMessage("");
    setError("");

    try {
      const response = await fetch("/api/auth/resend-verification", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resendEmail }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Erro ao reenviar verificação.");
        return;
      }

      // Caso: email já verificado mas sem senha → ir direto para set-password
      if (data.data?.alreadyVerified && data.data?.setPasswordToken) {
        setUserData(data.data);
        setStep("set-password");
        return;
      }

      // Caso: email já verificado e tem senha → ir para login
      if (data.data?.alreadyVerified && data.data?.hasPassword) {
        setResendMessage("Sua conta já está ativa. Redirecionando para login...");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      // Caso: novo token gerado → mostra mensagem de sucesso
      setResendMessage("Email de verificação reenviado! Verifique sua caixa de entrada e spam.");
      setStep("resend-success");
    } catch (err) {
      console.error("Resend error:", err);
      setError("Erro de conexão. Tente novamente.");
    } finally {
      setIsResending(false);
    }
  }

  async function handleSetPassword(e) {
    e.preventDefault();
    if (!isPasswordValid) return;

    setIsLoading(true);
    setError("");

    try {
      const response = await fetch("/api/auth/set-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          setPasswordToken: userData.setPasswordToken,
          password,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Erro ao definir senha.");
        return;
      }

      setStep("success");

      // Redirecionar para dashboard após 3 segundos
      setTimeout(() => {
        navigate("/internal/dashboard", { replace: true });
      }, 3000);
    } catch (err) {
      console.error("Set password error:", err);
      setError("Erro de conexão. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  }

  const PasswordCheck = ({ valid, label }) => (
    <div className="flex items-center gap-2 text-sm">
      {valid ? (
        <CheckCircle2 size={14} className="text-emerald-500" />
      ) : (
        <XCircle size={14} className="text-zinc-500" />
      )}
      <span className={valid ? "text-emerald-400" : "text-zinc-500"}>
        {label}
      </span>
    </div>
  );

  return (
    <div className="flex min-h-screen flex-col items-center justify-center p-6 md:p-10 bg-background text-foreground font-['Space_Grotesk'] relative overflow-hidden">
      <ParticleBackground />
      <div className="relative z-20 w-full max-w-sm md:max-w-4xl">
        <Card className="overflow-hidden border border-white/5 bg-surface/95 backdrop-blur-sm shadow-2xl">
          <CardContent className="grid p-0 md:grid-cols-2">
            {/* Form Side */}
            <div className="p-6 md:p-8 flex flex-col justify-center">
              {/* STEP: Verifying */}
              {step === "verifying" && (
                <div className="flex flex-col items-center gap-4 py-12">
                  <Loader2 size={48} className="text-cyan-500 animate-spin" />
                  <h1 className="text-xl font-bold text-foreground-strong">
                    Verificando seu email...
                  </h1>
                  <p className="text-muted-foreground text-sm text-center">
                    Aguarde enquanto validamos seu token de verificação.
                  </p>
                </div>
              )}

              {/* STEP: Set Password */}
              {step === "set-password" && (
                <form onSubmit={handleSetPassword} className="flex flex-col gap-5">
                  <div className="flex flex-col items-center gap-2 text-center mb-4">
                    <div className="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mb-2">
                      <CheckCircle2 size={24} className="text-emerald-500" />
                    </div>
                    <h1 className="text-2xl font-bold text-foreground-strong">
                      Email verificado!
                    </h1>
                    <p className="text-muted-foreground text-sm">
                      Olá, <span className="text-cyan-400">{userData?.companyName}</span>. Defina sua senha para acessar a plataforma.
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="password" className="text-foreground-strong">
                      Senha
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="Crie uma senha forte"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className="bg-background border-border text-foreground-strong placeholder:text-placeholder pr-10 focus-visible:ring-cyan-500 h-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground-strong"
                      >
                        {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="confirmPassword" className="text-foreground-strong">
                      Confirmar senha
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        placeholder="Digite a senha novamente"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        className="bg-background border-border text-foreground-strong placeholder:text-placeholder pr-10 focus-visible:ring-cyan-500 h-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground-strong"
                      >
                        {showConfirmPassword ? (
                          <EyeOff size={16} />
                        ) : (
                          <Eye size={16} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Password Validation */}
                  <div className="grid grid-cols-2 gap-2 bg-background rounded-lg p-3 border border-border">
                    <PasswordCheck
                      valid={passwordChecks.length}
                      label="8+ caracteres"
                    />
                    <PasswordCheck
                      valid={passwordChecks.uppercase}
                      label="1 maiúscula"
                    />
                    <PasswordCheck
                      valid={passwordChecks.number}
                      label="1 número"
                    />
                    <PasswordCheck
                      valid={passwordChecks.match}
                      label="Senhas iguais"
                    />
                  </div>

                  {error && (
                    <div className="text-sm text-red-500 text-center">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    disabled={!isPasswordValid || isLoading}
                    className="w-full bg-cyan-600 hover:bg-cyan-500 text-white font-semibold h-10 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin mr-2" />
                        Salvando...
                      </>
                    ) : (
                      "Definir senha e entrar"
                    )}
                  </Button>
                </form>
              )}

              {/* STEP: Success */}
              {step === "success" && (
                <div className="flex flex-col items-center gap-4 py-12">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    <CheckCircle2 size={40} className="text-emerald-500" />
                  </div>
                  <h1 className="text-2xl font-bold text-foreground-strong">
                    Tudo pronto!
                  </h1>
                  <p className="text-muted-foreground text-sm text-center">
                    Sua conta está ativa. Redirecionando para a plataforma...
                  </p>
                  <Loader2 size={20} className="text-cyan-500 animate-spin mt-2" />
                </div>
              )}

              {/* STEP: Error */}
              {step === "error" && (
                <div className="flex flex-col items-center gap-4 py-8">
                  <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
                    <XCircle size={40} className="text-red-500" />
                  </div>
                  <h1 className="text-xl font-bold text-foreground-strong">
                    Erro na verificação
                  </h1>
                  <p className="text-muted-foreground text-sm text-center max-w-xs">
                    {error}
                  </p>

                  {/* Resend verification form */}
                  <form onSubmit={handleResendVerification} className="w-full max-w-xs mt-2 flex flex-col gap-3">
                    <Label htmlFor="resendEmail" className="text-foreground-strong text-sm">
                      Reenviar verificação
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        id="resendEmail"
                        type="email"
                        placeholder="Seu email cadastrado"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        required
                        className="bg-background border-border text-foreground-strong placeholder:text-placeholder focus-visible:ring-cyan-500 h-9 text-sm"
                      />
                      <Button
                        type="submit"
                        disabled={isResending || !resendEmail}
                        size="sm"
                        className="bg-cyan-600 hover:bg-cyan-500 text-white h-9 px-3 shrink-0"
                      >
                        {isResending ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <RefreshCw size={14} />
                        )}
                      </Button>
                    </div>
                    {resendMessage && (
                      <p className="text-emerald-400 text-xs text-center">{resendMessage}</p>
                    )}
                  </form>

                  <div className="flex gap-3 mt-2">
                    <Button
                      variant="outline"
                      onClick={() => navigate("/login")}
                      className="border-border hover:bg-surface-hover bg-background text-foreground-strong"
                    >
                      Ir para Login
                    </Button>
                    <Button
                      onClick={() =>
                        window.open("https://wa.me/551152867786", "_blank")
                      }
                      className="bg-cyan-600 hover:bg-cyan-500 text-white"
                    >
                      Falar com Suporte
                    </Button>
                  </div>
                </div>
              )}

              {/* STEP: Resend Success */}
              {step === "resend-success" && (
                <div className="flex flex-col items-center gap-4 py-12">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center">
                    <Mail size={40} className="text-emerald-500" />
                  </div>
                  <h1 className="text-xl font-bold text-foreground-strong">
                    Email reenviado!
                  </h1>
                  <p className="text-muted-foreground text-sm text-center max-w-xs">
                    Enviamos um novo link de verificação para <span className="text-cyan-400">{resendEmail}</span>. Verifique sua caixa de entrada e spam.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => navigate("/login")}
                    className="border-border hover:bg-surface-hover bg-background text-foreground-strong mt-4"
                  >
                    Ir para Login
                  </Button>
                </div>
              )}
            </div>

            {/* Branding Side */}
            <div className="relative hidden md:flex flex-col items-center justify-center p-8 bg-[#03030a] overflow-hidden border-l border-white/5">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 to-purple-900/10 z-0"></div>
              <div className="relative z-20 flex flex-col items-center gap-6 text-center max-w-[280px]">
                <img
                  src={logo}
                  alt="Touchworks Logo"
                  className="h-8 w-auto mb-4"
                />
                {step === "set-password" && (
                  <blockquote className="space-y-2">
                    <p className="text-lg font-medium text-foreground-strong">
                      &ldquo;Defina sua senha e comece a transformar seus
                      atendimentos com IA.&rdquo;
                    </p>
                    <footer className="text-sm text-muted-foreground">
                      Equipe Touchworks
                    </footer>
                  </blockquote>
                )}
                {step === "success" && (
                  <blockquote className="space-y-2">
                    <p className="text-lg font-medium text-foreground-strong">
                      &ldquo;Sua jornada na automação inteligente começa
                      agora.&rdquo;
                    </p>
                    <footer className="text-sm text-muted-foreground">
                      Equipe Touchworks
                    </footer>
                  </blockquote>
                )}
                {(step === "verifying" || step === "error") && (
                  <blockquote className="space-y-2">
                    <p className="text-lg font-medium text-foreground-strong">
                      &ldquo;Você não precisa só de ferramenta, precisa de
                      método e estratégia.&rdquo;
                    </p>
                    <footer className="text-sm text-muted-foreground">
                      Wenendy Silva
                    </footer>
                  </blockquote>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="text-center text-xs text-muted-foreground mt-6">
          Ao continuar, você concorda com nossos{" "}
          <a href="#" className="underline hover:text-foreground-strong">
            Termos de Serviço
          </a>{" "}
          e{" "}
          <a href="#" className="underline hover:text-foreground-strong">
            Política de Privacidade
          </a>
          .
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-pink-500 via-red-500 to-yellow-500 z-50"></div>
    </div>
  );
}
